/**
 * useSubscription Hook
 *
 * Combines subscription store and payment service for a unified API.
 * Handles subscription state management, upgrades, downgrades, and cancellation.
 */

import { useState, useCallback, useMemo } from 'react';
import * as Linking from 'expo-linking';
import { openExternalUrl } from '@/lib/linking';
import { mode } from '@/lib/config/app-mode';

import { useSubscriptionStore } from '../state/subscription-store';
import { useProfileStore } from '../state/profile-store';
import { paymentService } from './payment-service';
import {
  updateSubscription as updateSubscriptionEdge,
  cancelSubscription as cancelSubscriptionEdge,
  reactivateSubscription as reactivateSubscriptionEdge,
} from '../supabase/edge-functions';
import type {
  Subscription,
  SubscriptionTier,
  SubscriptionStatus,
  BillingInterval,
  StartupTier,
  InvestorTier,
} from '../subscription/types';
import {
  isTierHigher,
  getTierConfig,
  isSubscriptionActive,
} from '../subscription/types';
import type { FeatureKey } from '../subscription/features';
import { formatPrice, formatPriceWithInterval } from '../subscription/pricing';

// ============================================================================
// Types
// ============================================================================

export interface UseSubscriptionReturn {
  // Current state
  subscription: Subscription | null;
  currentTier: SubscriptionTier;
  isActive: boolean;
  isPastDue: boolean;
  isCanceled: boolean;
  isTrialing: boolean;
  cancelAtPeriodEnd: boolean;

  // Feature access
  hasFeature: (feature: FeatureKey) => boolean;
  getFeatureLimit: (feature: FeatureKey) => number;

  // Pricing helpers
  getPrice: (tier: SubscriptionTier, interval: BillingInterval) => number;
  formatTierPrice: (tier: SubscriptionTier, interval: BillingInterval) => string;

  // Actions
  subscribe: (tier: SubscriptionTier, interval: BillingInterval) => Promise<SubscribeResult>;
  upgrade: (newTier: SubscriptionTier) => Promise<UpgradeResult>;
  downgrade: (newTier: SubscriptionTier) => Promise<DowngradeResult>;
  cancel: (params?: { reason?: string; feedback?: string }) => Promise<CancelResult>;
  reactivate: () => Promise<ReactivateResult>;
  openBillingPortal: () => Promise<void>;
  retryPayment: () => Promise<RetryResult>;

  // Loading states
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

interface SubscribeResult {
  success: boolean;
  error?: string;
}

interface UpgradeResult {
  success: boolean;
  previousTier?: SubscriptionTier;
  newTier?: SubscriptionTier;
  error?: string;
}

interface DowngradeResult {
  success: boolean;
  previousTier?: SubscriptionTier;
  newTier?: SubscriptionTier;
  effectiveDate?: string;
  error?: string;
}

interface CancelResult {
  success: boolean;
  effectiveDate?: string;
  error?: string;
}

interface ReactivateResult {
  success: boolean;
  error?: string;
}

interface RetryResult {
  success: boolean;
  error?: string;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useSubscription(): UseSubscriptionReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Store selectors
  const subscription = useSubscriptionStore((s) => s.subscription);
  const setSubscription = useSubscriptionStore((s) => s.setSubscription);
  const hasFeatureStore = useSubscriptionStore((s) => s.hasFeature);
  const getFeatureLimitStore = useSubscriptionStore((s) => s.getFeatureLimit);
  const getCurrentTier = useSubscriptionStore((s) => s.getCurrentTier);
  const stripeCustomerId = useSubscriptionStore((s) => s.stripeCustomerId);
  const setStripeCustomerId = useSubscriptionStore((s) => s.setStripeCustomerId);
  const storeUserRole = useSubscriptionStore((s) => s.userRole);

  // Get user role from main store if not set in subscription store
  const inverioUserRole = useProfileStore((s) => s.userRole);
  // Admin users don't use Stripe — fall back to 'startup' for type compatibility
  const rawRole = storeUserRole || inverioUserRole || 'startup';
  const userRole: 'startup' | 'investor' = rawRole === 'admin' ? 'startup' : (rawRole as 'startup' | 'investor');

  // Sync user role to subscription store
  const setStoreUserRole = useSubscriptionStore((s) => s.setUserRole);
  if (inverioUserRole && inverioUserRole !== 'pending' && !storeUserRole) {
    setStoreUserRole(inverioUserRole);
  }

  // Derived state
  const currentTier = getCurrentTier();
  const isActive = subscription ? isSubscriptionActive(subscription.status) : false;
  const isPastDue = subscription?.status === 'past_due';
  const isCanceled = subscription?.status === 'canceled';
  const isTrialing = subscription?.status === 'trialing';
  const cancelAtPeriodEnd = subscription?.cancelAtPeriodEnd ?? false;

  // ========================================================================
  // Feature Access
  // ========================================================================

  const hasFeature = useCallback(
    (feature: FeatureKey) => hasFeatureStore(feature),
    [hasFeatureStore]
  );

  const getFeatureLimit = useCallback(
    (feature: FeatureKey) => getFeatureLimitStore(feature),
    [getFeatureLimitStore]
  );

  // ========================================================================
  // Pricing Helpers
  // ========================================================================

  const getPrice = useCallback(
    (tier: SubscriptionTier, interval: BillingInterval) => {
      return paymentService.getSubscriptionPrice(tier, interval, userRole);
    },
    [userRole]
  );

  const formatTierPrice = useCallback(
    (tier: SubscriptionTier, interval: BillingInterval) => {
      const price = getPrice(tier, interval);
      return formatPriceWithInterval(price, interval);
    },
    [getPrice]
  );

  // ========================================================================
  // Actions
  // ========================================================================

  const subscribe = useCallback(
    async (
      tier: SubscriptionTier,
      interval: BillingInterval
    ): Promise<SubscribeResult> => {
      setIsLoading(true);
      setError(null);

      try {
        // For free tier, use mock implementation
        if (tier === 'free') {
          const result = await paymentService.createSubscription({
            tier,
            billingInterval: interval,
            userRole,
            customerId: stripeCustomerId ?? undefined,
          });

          if (result.success && result.subscription) {
            setSubscription(result.subscription);
            if (result.subscription.stripeCustomerId) {
              setStripeCustomerId(result.subscription.stripeCustomerId);
            }
            return { success: true };
          }

          const errorMsg = result.error || 'Failed to create subscription';
          setError(errorMsg);
          return { success: false, error: errorMsg };
        }

        // For paid tiers, use Stripe Checkout (or mock in mock mode)
        if (!mode.isMock) {
          // Live mode: redirect to Stripe Checkout
          // Use window.location.origin on web, Linking.createURL on native
          const baseUrl = typeof window !== 'undefined' && window.location
            ? `${window.location.origin}/`
            : Linking.createURL('/');
          const checkoutResult = await paymentService.createCheckoutSession({
            tier,
            billingInterval: interval,
            userRole,
            successUrl: `${baseUrl}subscription-success`,
            cancelUrl: `${baseUrl}subscription-cancel`,
          });

          // Open Stripe Checkout in browser
          await openExternalUrl(checkoutResult.url, 'useSubscription.openCheckout');

          // Return success - the webhook will update the subscription
          return { success: true };
        }

        // Demo mode: use mock implementation
        const result = await paymentService.createSubscription({
          tier,
          billingInterval: interval,
          userRole,
          customerId: stripeCustomerId ?? undefined,
        });

        if (result.success && result.subscription) {
          setSubscription(result.subscription);
          if (result.subscription.stripeCustomerId) {
            setStripeCustomerId(result.subscription.stripeCustomerId);
          }
          return { success: true };
        }

        const errorMsg = result.error || 'Failed to create subscription';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Subscription failed';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      } finally {
        setIsLoading(false);
      }
    },
    [userRole, stripeCustomerId, setSubscription, setStripeCustomerId]
  );

  const upgrade = useCallback(
    async (newTier: SubscriptionTier): Promise<UpgradeResult> => {
      if (!subscription) {
        // No existing subscription, create new one
        const result = await subscribe(newTier, 'month');
        return {
          success: result.success,
          newTier: result.success ? newTier : undefined,
          error: result.error,
        };
      }

      // Verify it's actually an upgrade
      if (!isTierHigher(newTier, subscription.tier, userRole)) {
        return {
          success: false,
          error: 'Cannot upgrade to a lower or same tier',
        };
      }

      setIsLoading(true);
      setError(null);

      try {
        const previousTier = subscription.tier;

        // In live mode with existing Stripe subscription, use Edge Function
        if (!mode.isMock && subscription.stripeSubscriptionId) {

          // Validate tier is valid for paid plans
          if (!['starter', 'standard', 'premium'].includes(newTier)) {
            setError('Invalid tier for upgrade');
            return { success: false, error: 'Invalid tier for upgrade' };
          }

          const { data, error: edgeError } = await updateSubscriptionEdge({
            newTier: newTier as 'starter' | 'standard' | 'premium',
            billingInterval: subscription.billingInterval,
          });

          if (edgeError || !data?.success) {
            const errorMsg = edgeError || data?.error || 'Failed to upgrade subscription';
            setError(errorMsg);
            return { success: false, error: errorMsg };
          }

          // Update local state with new subscription data
          if (data.subscription) {
            setSubscription({
              ...subscription,
              tier: data.subscription.tier as SubscriptionTier,
              status: data.subscription.status as SubscriptionStatus,
              currentPeriodStart: data.subscription.currentPeriodStart,
              currentPeriodEnd: data.subscription.currentPeriodEnd,
            });
          }

          return {
            success: true,
            previousTier,
            newTier,
          };
        }

        // Demo mode: use mock implementation
        const result = await paymentService.updateSubscription({
          subscriptionId: subscription.id,
          newTier,
          userRole,
        });

        if (result.success && result.subscription) {
          setSubscription(result.subscription);
          return {
            success: true,
            previousTier,
            newTier,
          };
        }

        const errorMsg = result.error || 'Failed to upgrade subscription';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Upgrade failed';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      } finally {
        setIsLoading(false);
      }
    },
    [subscription, userRole, subscribe, setSubscription]
  );

  const downgrade = useCallback(
    async (newTier: SubscriptionTier): Promise<DowngradeResult> => {
      if (!subscription) {
        return {
          success: false,
          error: 'No active subscription to downgrade',
        };
      }

      // Verify it's actually a downgrade
      if (isTierHigher(newTier, subscription.tier, userRole)) {
        return {
          success: false,
          error: 'Cannot downgrade to a higher tier',
        };
      }

      setIsLoading(true);
      setError(null);

      try {
        const previousTier = subscription.tier;

        // In live mode with existing Stripe subscription, use Edge Function
        if (!mode.isMock && subscription.stripeSubscriptionId) {
          // Validate tier is valid for paid plans
          if (!['starter', 'standard', 'premium'].includes(newTier)) {
            setError('Invalid tier for downgrade');
            return { success: false, error: 'Invalid tier for downgrade' };
          }

          const { data, error: edgeError } = await updateSubscriptionEdge({
            newTier: newTier as 'starter' | 'standard' | 'premium',
            billingInterval: subscription.billingInterval,
          });

          if (edgeError || !data?.success) {
            const errorMsg = edgeError || data?.error || 'Failed to downgrade subscription';
            setError(errorMsg);
            return { success: false, error: errorMsg };
          }

          // Update local state with new subscription data
          if (data.subscription) {
            setSubscription({
              ...subscription,
              tier: data.subscription.tier as SubscriptionTier,
              status: data.subscription.status as SubscriptionStatus,
              currentPeriodStart: data.subscription.currentPeriodStart,
              currentPeriodEnd: data.subscription.currentPeriodEnd,
            });
          }

          return {
            success: true,
            previousTier,
            newTier,
            effectiveDate: subscription.currentPeriodEnd,
          };
        }

        // Demo mode: use mock implementation
        const result = await paymentService.updateSubscription({
          subscriptionId: subscription.id,
          newTier,
          userRole,
        });

        if (result.success && result.subscription) {
          setSubscription(result.subscription);
          return {
            success: true,
            previousTier,
            newTier,
            effectiveDate: subscription.currentPeriodEnd,
          };
        }

        const errorMsg = result.error || 'Failed to downgrade subscription';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Downgrade failed';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      } finally {
        setIsLoading(false);
      }
    },
    [subscription, userRole, setSubscription]
  );

  const cancel = useCallback(async (params?: { reason?: string; feedback?: string }): Promise<CancelResult> => {
    const { reason, feedback } = params || {};

    if (!subscription) {
      return {
        success: false,
        error: 'No active subscription to cancel',
      };
    }

    setIsLoading(true);
    setError(null);

    try {
      // Combine reason and feedback for Stripe metadata
      const fullReason = feedback ? `${reason}: ${feedback}` : reason;

      // In live mode with existing Stripe subscription, use Edge Function
      if (!mode.isMock && subscription.stripeSubscriptionId) {
        const { data, error: edgeError } = await cancelSubscriptionEdge({
          cancelImmediately: false, // Best practice: cancel at period end
          reason: fullReason,
        });

        if (edgeError || !data?.success) {
          const errorMsg = edgeError || data?.error || 'Failed to cancel subscription';
          setError(errorMsg);
          return { success: false, error: errorMsg };
        }

        // Update local state
        if (data.subscription) {
          setSubscription({
            ...subscription,
            status: data.subscription.status as SubscriptionStatus,
            cancelAtPeriodEnd: data.subscription.cancelAtPeriodEnd,
            canceledAt: data.subscription.canceledAt || undefined,
          });
        }

        return {
          success: true,
          effectiveDate: data.subscription?.currentPeriodEnd || subscription.currentPeriodEnd,
        };
      }

      // Demo mode: use mock implementation
      const result = await paymentService.cancelSubscription({
        subscriptionId: subscription.id,
        cancelImmediately: false,
      });

      if (result.success) {
        setSubscription({
          ...subscription,
          cancelAtPeriodEnd: true,
          canceledAt: new Date().toISOString(),
        });
        return {
          success: true,
          effectiveDate: subscription.currentPeriodEnd,
        };
      }

      const errorMsg = result.error || 'Failed to cancel subscription';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Cancellation failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, [subscription, setSubscription]);

  const reactivate = useCallback(async (): Promise<ReactivateResult> => {
    if (!subscription) {
      return {
        success: false,
        error: 'No subscription to reactivate',
      };
    }

    if (!subscription.cancelAtPeriodEnd) {
      return {
        success: false,
        error: 'Subscription is not scheduled for cancellation',
      };
    }

    setIsLoading(true);
    setError(null);

    try {
      // In live mode with existing Stripe subscription, use Edge Function
      if (!mode.isMock && subscription.stripeSubscriptionId) {
        const { data, error: edgeError } = await reactivateSubscriptionEdge();

        if (edgeError || !data?.success) {
          const errorMsg = edgeError || data?.error || 'Failed to reactivate subscription';
          setError(errorMsg);
          return { success: false, error: errorMsg };
        }

        // Update local state
        if (data.subscription) {
          setSubscription({
            ...subscription,
            status: data.subscription.status as SubscriptionStatus,
            cancelAtPeriodEnd: data.subscription.cancelAtPeriodEnd,
            canceledAt: undefined,
          });
        }

        return { success: true };
      }

      // Demo mode: use mock implementation
      const result = await paymentService.reactivateSubscription(subscription.id);

      if (result.success) {
        setSubscription({
          ...subscription,
          cancelAtPeriodEnd: false,
          canceledAt: undefined,
        });
        return { success: true };
      }

      const errorMsg = result.error || 'Failed to reactivate subscription';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Reactivation failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, [subscription, setSubscription]);

  const openBillingPortal = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Use window.location.origin on web, Linking.createURL on native
      const returnUrl = typeof window !== 'undefined' && window.location
        ? `${window.location.origin}/settings`
        : Linking.createURL('/settings');
      const result = await paymentService.getCustomerPortalUrl(
        stripeCustomerId ?? undefined,
        returnUrl
      );
      await openExternalUrl(result.url, 'useSubscription.openPortal');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to open billing portal';
      setError(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [stripeCustomerId]);

  const retryPayment = useCallback(async (): Promise<RetryResult> => {
    if (!subscription) {
      return {
        success: false,
        error: 'No subscription found',
      };
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await paymentService.retryPayment(subscription.id);

      if (result.success) {
        setSubscription({
          ...subscription,
          status: 'active',
        });
        return { success: true };
      }

      const errorMsg = result.error || 'Payment retry failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Payment retry failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, [subscription, setSubscription]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ========================================================================
  // Return
  // ========================================================================

  return useMemo(
    () => ({
      // State
      subscription,
      currentTier,
      isActive,
      isPastDue,
      isCanceled,
      isTrialing,
      cancelAtPeriodEnd,

      // Feature access
      hasFeature,
      getFeatureLimit,

      // Pricing
      getPrice,
      formatTierPrice,

      // Actions
      subscribe,
      upgrade,
      downgrade,
      cancel,
      reactivate,
      openBillingPortal,
      retryPayment,

      // Loading
      isLoading,
      error,
      clearError,
    }),
    [
      subscription,
      currentTier,
      isActive,
      isPastDue,
      isCanceled,
      isTrialing,
      cancelAtPeriodEnd,
      hasFeature,
      getFeatureLimit,
      getPrice,
      formatTierPrice,
      subscribe,
      upgrade,
      downgrade,
      cancel,
      reactivate,
      openBillingPortal,
      retryPayment,
      isLoading,
      error,
      clearError,
    ]
  );
}
